/**
 * Created by ppatton on 12/21/2016.
 */
import { Injectable } from '@angular/core';
import { AppConstants } from '@utils/app.constants';

@Injectable()
export class LMContextService {
  private context: LMContext;
  private webAppSpecs: LMWebAppSpec[];
  private _clearPageState = false;

  constructor() {
  }

  getContext(): LMContext {
    return this.context;
  }

  setContext(context: LMContext) {
    this.context = context;
  }

  updateUrlPdlOrDa(urlPdlOrDa: string) {
    this.context.urlPdlOrDa = urlPdlOrDa;
  }

  updateWebApp(webAppSpec: LMWebAppSpec) {
    this.context.webApp = webAppSpec.webAppName;
    this.context.webAppTitle = webAppSpec.webAppTitle;
    LandmarkContext.webApp = webAppSpec.webAppName;
    LandmarkContext.webAppTitle = webAppSpec.webAppTitle;
  }

  hasAsOfDateInPast(): boolean {
    return (this.context && this.context.asOfDate && this.context.asOfDateInPast);
  }

  getAsOfDate(): string {
    return this.context.asOfDate;
  }

  setAsOfDate(date: string) {
    this.context.asOfDate = date;
    if (!this.context.contextSessionKeys) {
      this.context.contextSessionKeys = {};
    }
    this.context.contextSessionKeys[ AppConstants.PARAM_CSK_ASOFDATE ] = date;
  }

  clearAsOfDate() {
    this.context.asOfDate = '';
    delete this.context.contextSessionKeys[ AppConstants.PARAM_CSK_ASOFDATE ];
  }

  getAsOfDateInPast(): boolean {
    return this.context.asOfDateInPast;
  }

  setAsOfDateInPast(asOfDateInPast: boolean) {
    this.context.asOfDateInPast = asOfDateInPast;
  }

  getShowDeletedRecords(): boolean {
    return this.context.showdeletedrecords;
  }

  setShowDeletedRecords(show: boolean) {
    this.context.showdeletedrecords = show;
    if (!this.context.contextSessionKeys) {
      this.context.contextSessionKeys = {};
    }
    if (show) {
      this.context.contextSessionKeys[ AppConstants.PARAM_CSK_SHOWDELETEDRECORDS ] = show;
    } else {
      delete this.context.contextSessionKeys[ AppConstants.PARAM_CSK_SHOWDELETEDRECORDS ];
    }
  }

  isBannerInRoute(): boolean {
    if (!this.context.contextSessionKeys) {
      this.context.contextSessionKeys = {};
    }
    const routeInBanner: string = this.context.contextSessionKeys[ AppConstants.PARAM_CSK_BANNERINROUTE ];
    return routeInBanner ? routeInBanner.toLowerCase() === 'true' : false;
  }

  getActorId(): string {
    return this.context.actorId;
  }

  getActorUniqueId(): string {
    return this.context.actorUniqueId;
  }

  getUIType(): LMUIType {
    return this.context.uiType;
  }

  hasConfigAccess(): boolean {
    const webApps = this.getAccessibleWebApps();
    if (!webApps) {
      return false;
    }
    const configArray: LMWebAppSpec[] = webApps.filter(webapp => {
      if (webapp.webAppName === 'ConfigurationConsole') {
        return webapp;
      }
    });

    return configArray.length > 0;
  }

  isMobileDevice(): boolean {
    return this.getUIType() === 'SMARTPHONE' || this.getUIType() === 'TABLET';
  }

  get configStamp(): number {
    return this.context.configStamp;
  }

  get uiLoadingIndicatorTimeout(): number {
    if (this.context?.uiLoadingIndicatorTimeout > 0) {
      // value times 1000 to get milliseconds
      return (this.context?.uiLoadingIndicatorTimeout * 1000);
    }

    return this.context?.uiLoadingIndicatorTimeout;
  }

  get uiPagingRequestWaitTime(): number {
    if (this.context.uiPagingRequestWaitTime > 0) {
      // value times 1000 to get milliseconds
      return (this.context.uiPagingRequestWaitTime * 1000);
    }

    return this.context.uiPagingRequestWaitTime;
  }

  get isUiPagingRequestWaitTimeEnabled(): boolean {
    return this.context.uiPagingRequestWaitTime > 0;
  }

  userDefinedStatesPageSizeAndIndicator(): boolean {
    return this.context.userDefinedStatePageSizeAndFilter;
  }

  userDefinedStatesDefaultPageSize(): number {
    return this.context.userDefinedStateDefaultPageSize;
  }

  casualMode(): boolean {
    if (this.context && this.context.contextSessionKeys) {
      return (this.context.contextSessionKeys[ 'csk.casualMode' ] === 'true');
    }

    return false;
  }

  isDeveloper(): boolean {
    return this.context.developer;
  }

  /**
   * Whether or not to display the context for a mingle
   * widget.
   * @returns {boolean}
   */

  inMingleWidget(): boolean {
    return this.context.inMingleWidget;
  }

  insideInforGo(): boolean {
    return this.context.insideInforGo;
  }

  inMingleInbox(): boolean {
    if (this.inMingle()) {
      return !!(this.context.contextSessionKeys && this.context.contextSessionKeys['csk.mi']);
    }
    return false;
  }

  isXiFull(): boolean {
    return this.context.uiHarness === 'xifull';
  }

  isXiHarness(): boolean {
    return this.context.uiHarness === 'xiharness';
  }

  is3xHarness(): boolean {
    return this.context.uiHarness === '3xharness';
  }

  isSharePageAnalyticsEnabled(): boolean {
    return !!(this.context && this.context.sharePageAnalytics && this.context.sharePageAnalytics.length > 0);
  }

  isMonocleEnabled(): boolean {

    // add multi tenant only for monocle
    return (this.context && this.context.enableUserPageHit && this.isSharePageAnalyticsEnabled());
  }

  getCompactnessMode(): LMCompactnessMode {
    if (this.context && this.context.compactnessMode) {
      return this.context.compactnessMode;
    }

    return 'compact';
  }

  testVersion(): LMTestVersion {
    if (this.context && this.context.testVersion) {
      return this.context.testVersion;
    }

    return 1;
  }

  isHideMenuBar(): boolean {
    if (this.context && this.context.contextSessionKeys) {
      return (this.context.contextSessionKeys[ 'csk.hidemenubar' ] === 'true');
    }

    return false;
  }

  isHideTitlebar(): boolean {
    if (this.context && this.context.contextSessionKeys) {
      return (this.context.contextSessionKeys[ 'csk.hidetitlebar' ] === 'true');
    }

    return false;
  }

  useLazyLoadForm(): boolean {
    if (this.context && this.context.contextSessionKeys && this.context.contextSessionKeys[ 'csk.uselazyloadform' ]) {
      return (this.context.contextSessionKeys[ 'csk.uselazyloadform' ] === 'true');
    }

    return true;
  }

  allowAnonymousAccess(): boolean {
    return this.context.allowAnonymousAccess;
  }

  isAnonymousUser(): boolean {
    return this.context.isAnonymousUser;
  }

  getLog(): number {
    if (this.context && this.context.contextSessionKeys) {
      const key = this.context.contextSessionKeys[ 'csk.log' ];
      const value = Number(key);

      if (isNaN(value)) {
        return 0;
      }

      if (value >= 1 && value <= 4) {
        return value;
      }
    }

    return 0;
  }

  getLogToClient(): boolean {
    if (this.context && this.context.contextSessionKeys) {
      const key = this.context.contextSessionKeys[ 'csk.logtoclient' ];
      const value = (key === 'true');
      return key ? value : false;
    }

    return false;
  }

  setAccessibleWebApps(webApps: LMWebAppSpec[]) {
    this.webAppSpecs = webApps;
  }

  getAccessibleWebApps(): LMWebAppSpec[] {
    return this.webAppSpecs;
  }

  getCurrentWebAppSpec(): LMWebAppSpec {
    if (this.context.webApp === 'web') {
      const webWebAppSpec: LMWebAppSpec = {
        webAppUrl: '',
        webAppName: 'web',
        webAppIconUrl: '',
        webAppDescription: '',
        webAppTitle: 'Web',
        navigationMenuUrl: '',
        showUsing: 'SoHo_Xi',
        menu: false
      };
      return webWebAppSpec;
    }

    const result: LMWebAppSpec[] = this.webAppSpecs.filter(webAppSpec => webAppSpec.webAppName === this.context.webApp);
    if (result.length !== 1) {
      throw 'cannot find webAppSpec for ' + this.context.webApp;
    }
    return result[ 0 ];
  }

  inMingle(): boolean {
    return this.getLogicalId().length > 0;
  }

  getLogicalId(): string {
    return (this.context && this.context.logicalId ? this.context.logicalId : '');
  }

  getWebpartScreenId(): string {
    return (this.context && this.context.webpartScreenId ? this.context.webpartScreenId : '');
  }

  getUrlPdlOrDa() {
    return (this.context && this.context.urlPdlOrDa ? this.context.urlPdlOrDa : '');
  }

  getWebApp() {
    return (this.context && this.context.webApp ? this.context.webApp : '');
  }

  getToastMessagePosition(): LMToastPosition {
    return (this.context?.uiToastMessagePosition ? this.context.uiToastMessagePosition : 'BLANK');
  }

  getToastMessageDuration(): LMToastDuration {
    return (this.context?.uiToastMessageDuration ? this.context.uiToastMessageDuration : 'BLANK');
  }

  /**
   * Placing here for a lack of a better spot. This is set in the banner when
   * navigating to a screen. That screen will have it's page state cleared.
   * Page state should only be used when going back and forward with the
   * browser button or through standard screen navigation.
   * @returns
   */
  getClearPageState(): boolean {
    return this.context ? this.context.clearPageState : this._clearPageState;
  }

  setClearNextPageState(clearPageState: boolean): void {
    this._clearPageState = clearPageState;
    if (this.context) {
      this.context.clearPageState = this._clearPageState;
    }
  }

  is3_5Content(appDisplay: string) {
    // default or 3_5 are for 3X as the default is 3x
    let content3_5 = this.getCurrentWebAppSpec().showUsing !== 'SoHo_Xi';
    let forceXi = 'false';
    if (this.context && this.context.contextSessionKeys) {
      forceXi = this.context.contextSessionKeys[ 'csk.showusingxi' ];
    }
    // First check to see if xi should be forced then if the app personalization is set to something override what is set in webapp
    if ((this.context && this.context.uiHarness === 'xifull') || forceXi === 'true') {
      content3_5 = false;
    } else if (appDisplay === '3_5') {
      content3_5 = true;
    } else if (appDisplay === '4_0' || appDisplay === 'override') {
      content3_5 = false;
    }

    return content3_5;
  }

  public useNewSpecUrls(): boolean {
    if (this.context && this.context.contextSessionKeys && this.context.contextSessionKeys[ 'csk.newspecurl' ]) {
      return (this.context.contextSessionKeys[ 'csk.newspecurl' ] === 'true');
    }

    return (this.context && this.context.useNewSpecUrls);
  }

  disablePageState(): boolean {
    if (this.context && this.context.contextSessionKeys && this.context.contextSessionKeys[ 'csk.disablepagestate' ]) {
      return (this.context.contextSessionKeys[ 'csk.disablepagestate' ] === 'true');
    }

    return false;
  }

  isGenProductLine(): boolean {
    if (this.context && this.context.contextSessionKeys && this.context.contextSessionKeys['csk.gen']) {
      return this.context.contextSessionKeys['csk.gen'] === 'true';
    }
    return false;
  }

  useSingleSelectList(): boolean {
    if (this.context && this.context.contextSessionKeys && this.context.contextSessionKeys[ 'csk.usesingleselectlist' ]) {
      return (this.context.contextSessionKeys[ 'csk.usesingleselectlist' ] === 'true');
    }

    return (this.context?.useSingleSelectList);
  }

  enableMultipleSearchTerms(): boolean {
    if (this.context && this.context.contextSessionKeys && this.context.contextSessionKeys[ 'csk.enablemultiplesearchterms' ]) {
      return (this.context.contextSessionKeys[ 'csk.enablemultiplesearchterms' ] === 'true');
    }

    return this.context?.enableMultipleSearchTerms;
  }

  useMultiSelectKeyField(): boolean {
    if (this.useSingleSelectList()) {
      if (this.enableMultipleSearchTerms()) {
        return true;
      }

      // also allow for csk.usemultiselectkeyfield since this older csk was communicated
      if (this.context && this.context.contextSessionKeys && this.context.contextSessionKeys[ 'csk.usemultiselectkeyfield' ]) {
        return (this.context.contextSessionKeys[ 'csk.usemultiselectkeyfield' ] === 'true');
      }
    }

    return false;
  }

  enableMaintainingSelectedValues(): boolean {
    if (!this.useSingleSelectList() || !this.enableMultipleSearchTerms()) {
      return false;
    }

    if (this.context && this.context.contextSessionKeys && this.context.contextSessionKeys[ 'csk.enablemaintainingselectedvalues' ]) {
      return (this.context.contextSessionKeys[ 'csk.enablemaintainingselectedvalues' ] === 'true');
    }

    return false;
  }

  enablePageSpecRefactor(): boolean {
    if (this.context) {

      if (this.context.contextSessionKeys
      &&  this.context.contextSessionKeys[ 'csk.pagespec-refactor' ]
      &&  this.context.contextSessionKeys[ 'csk.pagespec-refactor' ] === 'false') {
        return false;
      }

      if (this.context.pagespec_refactor !== undefined && this.context.pagespec_refactor === false) {
        return false;
      }
    }
    return true;
  }

  isStepUpAuthetication(): boolean {
    if (this.context && this.context.stepUpAuthentication !== undefined && this.context.stepUpAuthentication) {
      // This flag is only for one time consumption.
      this.resetStepUpAuthentication();
      return true;
    }
    return false;
  }

  isApplicationMenuV2() {
    if (this.isMobileDevice() && this.context.uiMenuVersion === undefined) {
      if (this.context.allowAnonymousAccess) {
        return this.context.uiExternalMenuVersion === 'v2';
      }

      return true;
    }

    if (this.context.allowAnonymousAccess) {
      return this.context.uiExternalMenuVersion === 'v2';
    }

    return this.context.uiMenuVersion === 'v2';
  }

  isApplicationMenuV1() {
    if (this.isMobileDevice() && this.context.uiMenuVersion === undefined) {
      if (this.context.allowAnonymousAccess) {
        return this.context.uiExternalMenuVersion !== 'v2';
      }

      return false;
    }

    if (this.context.allowAnonymousAccess) {
      return this.context.uiExternalMenuVersion !== 'v2';
    }

    return this.context.uiMenuVersion !== 'v2';
  }

  private resetStepUpAuthentication() {
    this.context.stepUpAuthentication = false;
  }

  hasAccessibilityModeParameter(): boolean {
    return this.context?.contextSessionKeys?.['csk.accessibility'] !== undefined;
  }

  getAccessibilityModeParameterValue(): boolean {
    return this.context?.contextSessionKeys?.['csk.accessibility'] === 'true';
  }

  hasDBSessionDebugMode(): boolean {
    return this.context?.contextSessionKeys?.['csk.dbsessiondebug'] !== undefined;
  }

  isSecSessionDebugMode(): boolean {
    return this.context?.contextSessionKeys?.['csk.secsessiondebug']  === 'true';
  }

  getDBSessionDebugId(): string {
    return this.context?.contextSessionKeys?.['csk.dbsessiondebug'];
  }

  hasDBDebugLevel(): boolean {
    return this.context?.contextSessionKeys?.['csk.dbsessiondebuglevel'] !== undefined;
  }

  getDBDebugLevel(): string {
    return this.context?.contextSessionKeys?.['csk.dbsessiondebuglevel'];
  }

  hasDBSessionTrace(): boolean {
    return this.context?.contextSessionKeys?.['csk.lpltrace'] !== undefined;
  }

  getDBSessionTrace(): boolean {
    return this.context?.contextSessionKeys?.['csk.lpltrace'] === 'true';
  }

  isConfigFeatureEnabled(featureName: string): boolean {
    return this.context.configuredFeatures.indexOf(featureName) !== -1;
  }

  getFilterBarModeSetInCsk(): string {
    return this.context?.contextSessionKeys?.[ 'csk.filterbarmode' ];
  }

  isFilterBarOnPowerMode(): boolean {
    if (this.context?.allowAnonymousAccess) {
      return this.context?.uiExternalAppFilterBarMode === 'power';
    }

    return this.context?.uiAppFilterBarMode === 'power';
  }

  public disableTreeFilterFix(): boolean {
    return this.context?.disableTreeFilterFix;
  }

  showUserListView(): boolean {
    // Using configuration parameter UIEnableSavedViews
    // return this.context?.contextSessionKeys?.['csk.savedSearch'] === 'true'
    const enableSavedSearch = this.context?.enableSavedSearch || false;
    return enableSavedSearch && !this.isAnonymousUser() && !this.casualMode();
  }

  public menuSearchOpenIndex(): string {
    return this.context?.menuSearchOpenIndex;
  }

  public pdfPollEnabled(): boolean {
    return this.context?.contextSessionKeys?.[ 'csk.pollforpdf'];
  }

  public setMingleActivityCookie() {
    // Moving it to Context Service from Context Util. Angular creates the service objects for injection
    // and context util was created before context service resulting in issues related to registering Ming.le handlers.
    const data = {
      applicationName: this.getContext().webApp,
      updateCookie: true,
    };
    infor.companyon.client.sendMessage('updateUserActivityCookie', data);
  }


}
